import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface Team {
  id: number;
  team_name: string;
  team_logo: string;
  table_name: string;
  created_at: string;
  futname: string;
  ga: string;
  gf: string;
  gd: string;
  pts: string;
  reds: string;
  status: string; // This will be used to categorize teams by group
  totalscores: string;
  yellows: string;
  [key: string]: any;
}

const TeamsDisplay: React.FC = () => {
  const [teamsData, setTeamsData] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Define predefined categories for table names
  const predefinedCategories: { [key: string]: string } = {
    'teams': 'Football',
    'netteams': 'Netball',
    'volteams': 'Volleyball Brothers',
    'volsteams': 'Volleyball Sisters',
    'basteams': 'Basketball',
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get<Team[]>('https://tronova.azmiproductions.com/api-aivg/table-api.php');
        setTeamsData(response.data);
        console.log(response.data);
      } catch (error) {
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="text-center py-10 text-xl text-white">Loading...</div>;
  if (error) return <div className="text-center py-10 text-xl text-red-600">{error}</div>;

  // Group teams by predefined categories, then by groups (e.g., Group A, Group B) within Football
  const groupedTeams = teamsData.reduce((acc: { [key: string]: { [key: string]: Team[] } }, team) => {
    const category = predefinedCategories[team.table_name.toLowerCase()] || 'Others';

    // Initialize the main category if it doesn't exist
    if (!acc[category]) acc[category] = {};

    // For Football, create further sub-groups based on 'status' (e.g., Group A, Group B)
    const subGroup = category === 'Football' ? team.status : 'General';
    (acc[category][subGroup] = acc[category][subGroup] || []).push(team);

    return acc;
  }, {});

  // Sort teams within each subgroup by points (pts), then by totalscores, and finally by goal difference (gd) in descending order
// Sort teams within each subgroup by points (pts), then by goal difference (gd), and finally by total scores (totalscores) in descending order
for (const category in groupedTeams) {
    for (const subGroup in groupedTeams[category]) {
      groupedTeams[category][subGroup].sort((a, b) => {
        // Parse values to integers or use 0 if undefined or NaN
        const ptsA = parseInt(a.pts, 10) || 0;
        const ptsB = parseInt(b.pts, 10) || 0;
        const gdA = parseInt(a.gd, 10) || 0;
        const gdB = parseInt(b.gd, 10) || 0;
        const totalscoresA = parseInt(a.totalscores, 10) || 0;
        const totalscoresB = parseInt(b.totalscores, 10) || 0;
  
        // First, sort by points
        if (ptsB !== ptsA) {
          return ptsB - ptsA; // Higher points come first
        }
        
        // If points are the same, sort by goal difference
        if (gdB !== gdA) {
          return gdB - gdA; // Higher goal difference comes first
        }
  
        // If both points and goal difference are the same, sort by total scores
        return totalscoresB - totalscoresA; // Higher total scores come first
      });
    }
  }
  
  
  

  return (
    <div
      className="min-h-screen py-10 flex items-center justify-center"
      style={{
        backgroundImage: `url('https://aivg.azmiproductions.com/assets/BACKGROUND%20PIC%20PNG%20FORM.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="bg-black bg-opacity-30 rounded-lg shadow-lg p-8 text-center w-full max-w-screen-2xl">
        <div className="relative z-10 text-white">
          <div className="container mx-auto text-center mb-5">
            <img
              src="https://aivg.azmiproductions.com/assets/IMG_4621%20%283%29.PNG"
              alt="Logo"
              className="mx-auto mb-4 w-24 h-24"
            />
            <h1 className="text-4xl font-extrabold mb-2 uppercase">Ahmad Ibrahim Intervarsity Games 2024</h1>
            <h1 className="text-base font-extrabold mb-6 uppercase">powered by azp group sdn bhd</h1>
            <p className="text-lg font-bold uppercase">Team Standings</p>
            <p className="text-sm font-light uppercase">Rotate your phone for best experience</p>
          </div>

          <div className="space-y-10">
            {Object.keys(groupedTeams).map((categoryName) => (
              <div key={categoryName} className=" p-6 rounded-lg shadow-lg">
                <h2 className="text-2xl font-semibold mb-10 uppercase text-white">{categoryName}</h2>
                {Object.keys(groupedTeams[categoryName]).map((subGroupName) => (
                  <div key={subGroupName} className="mb-6">
                    {categoryName === 'Football' && <h3 className="text-xl font-bold mb-4 text-blue-300">{subGroupName}</h3>}
                    <div className="overflow-x-auto shadow-lg border border-gray-700 rounded-lg bg-gray-800">
                      <table className="min-w-full text-sm text-gray-300 w-full">
                        <thead>
                          <tr className="bg-gradient-to-r from-blue-950 via-blue-600 to-gray-950 border-b border-gray-600">
                            <th className="py-3 px-4 text-center uppercase">Rank</th>
                            <th className="py-3 px-4 text-center uppercase">Logo</th>
                            <th className="py-3 px-4 text-center uppercase">Team Name</th>
                            <th className="py-3 px-4 text-center">PTS</th>
                            
                            
                            <th className="py-3 px-4 text-center">GF</th>
                            <th className="py-3 px-4 text-center">GA</th>
                            <th className="py-3 px-4 text-center">GD</th>
                            <th className="py-3 px-4 text-center uppercase">yellows</th>
                            <th className="py-3 px-4 text-center uppercase">Reds</th>
                          </tr>
                        </thead>
                        <tbody>
  {groupedTeams[categoryName][subGroupName].map((team, index) => (
    <tr key={team.id} className="hover:bg-gray-700 border-b border-gray-600 transition ease-in-out duration-300">
      <td className="py-3 px-4">{index + 1}</td> {/* Displaying the rank */}
      <td className="py-3 px-4 flex justify-center">
        <div className="relative w-20 h-20">
          <img
            src={team.team_logo ?? 'https://aivg.azmiproductions.com/assets/IMG_4621%20%283%29.PNG'} // Replace with a default image URL if needed
            alt={team.team_name ?? '-'}
            className="rounded-full border-4 border-gray-500 shadow-lg transform hover:scale-110 transition-transform duration-300"
          />
        </div>
      </td>
      <td className="py-3 px-4">{team.futname ?? '-'}</td>
      <td className="py-3 px-4">{team.pts ?? '-'}</td>
      
      <td className="py-3 px-4">{team.totalscores ?? '-'}</td>
      <td className="py-3 px-4">{team.ga ?? '-'}</td>
      <td className="py-3 px-4">{team.gd ?? '-'}</td>
      <td className="py-3 px-4">{team.yellows ?? '-'}</td>
      <td className="py-3 px-4">{team.reds ?? '-'}</td>
    </tr>
  ))}
</tbody>

                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamsDisplay;
